import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import { replaceDocumentFile } from '@/api/generated';
import { FileDropZone } from '@/components/form/FileDropZone';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { useToast } from '@/components/ui/useToast';

import type { DocumentRow } from './documentLabels';
import { acceptAttribute, refuseFile, typesInWords } from './uploadRules';
import { useInvalidateDocuments, useUploadPolicy } from './useDocuments';

interface ReplaceFileDialogProps {
  /** The document whose file is replaced; kept while the dialog fades out. */
  document: DocumentRow | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Replace file (P13, PB7): one drop zone under the upload dialog's rules,
 * posting to `/documents/{id}/file`. The document keeps serving its current
 * file while the new one is scanned, which the toast says, and the row then
 * shows "Replacement scanning" from the refetched list.
 *
 * Refusals go where the upload dialog puts them: a 413 or 415 against the
 * file, which is cleared; a 409 (a replacement already pending, no clean
 * file, the document removed) to a toast with the dialog closed and the
 * library asked for again, since the row on screen was out of date; a 403
 * and anything else to an alert inside, with the file kept.
 *
 * The page mounts a fresh dialog for every replace, so nothing chosen for one
 * document is still chosen for the next.
 */
export function ReplaceFileDialog({ document, open, onClose }: ReplaceFileDialogProps) {
  const invalidate = useInvalidateDocuments();
  const toast = useToast();
  const policy = useUploadPolicy();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  const replace = useMutation({
    mutationFn: async (chosen: File) => {
      const reply = await replaceDocumentFile({
        path: { document_id: document?.id ?? '' },
        body: { file: chosen },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async () => {
      onClose();
      toast.show({
        message:
          'Replacement uploaded. The current file stays until the new one is scanned.',
      });
      await invalidate();
    },
    onError: async (error) => {
      if (error instanceof ApiError && (error.status === 413 || error.status === 415)) {
        setFile(null);
        setFileError(error.serverMessage ?? error.message);
        return;
      }
      if (error instanceof ApiError && error.status === 409) {
        onClose();
        toast.show({ message: error.serverMessage ?? error.message, severity: 'error' });
        await invalidate();
        return;
      }
      setFailure(
        error instanceof Error ? error.message : 'The file could not be replaced.',
      );
      // A 403 is said here with the file kept (error contract), and the
      // library asked for again so the offered actions are reviewed.
      if (error instanceof ApiError && error.status === 403) await invalidate();
    },
  });

  const askToClose = () => {
    if (replace.isPending) return;
    onClose();
  };

  function choose(chosen: File) {
    if (!policy.data) return;
    const refused = refuseFile(chosen, policy.data);
    setFileError(refused);
    setFile(refused ? null : chosen);
  }

  const hint = policy.data
    ? `${typesInWords(policy.data)}, up to ${policy.data.maxUploadMb} MB.`
    : 'Checking which files are accepted…';

  return (
    <Dialog
      open={open}
      onClose={askToClose}
      title="Replace file"
      description={`The new file replaces the one under “${document?.title ?? ''}” once its scan is clean. Until then the current file is still served.`}
      testId="replace-file-dialog"
      actions={
        <>
          <Button
            variant="secondary"
            onClick={askToClose}
            disabled={replace.isPending}
            data-testid="replace-file-dialog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={file === null || replace.isPending}
            loading={replace.isPending}
            onClick={() => file && replace.mutate(file)}
            data-testid="replace-file-dialog-confirm-button"
          >
            Upload replacement
          </Button>
        </>
      }
    >
      <Stack spacing={2.5} data-testid="replace-file-dialog-form" sx={{ pt: 0.5 }}>
        {failure ? (
          <Alert
            severity="error"
            role="alert"
            ref={alertRef}
            tabIndex={-1}
            data-testid="replace-file-dialog-error"
          >
            {failure}
          </Alert>
        ) : null}

        {policy.isError ? (
          <ErrorState
            title="The upload rules could not be loaded."
            onRetry={() => void policy.refetch()}
            testId="replace-file-dialog-policy"
          />
        ) : (
          <FileDropZone
            label="New file"
            hint={hint}
            accept={policy.data ? acceptAttribute(policy.data) : ''}
            value={file}
            onChange={choose}
            onRemove={() => {
              setFile(null);
              setFileError(null);
            }}
            error={fileError}
            disabled={!policy.data}
            disabledReason="Loading the upload rules…"
            busy={replace.isPending}
            busyMessage={file ? `Uploading ${file.name}` : undefined}
            testId="replace-file-dialog-file"
          />
        )}
      </Stack>
    </Dialog>
  );
}
